import type { Page } from "@emkit/builder-core";

export interface Template {
  id: string;
  name: string;
  description: string;
  page: Page;
}

// Templates ship as part of the editor for now. When we get >20 or
// user-contributed ones, extract to @emkit/builder-templates.
//
// Each template is loadable via editor.setPage(template.page).
// IDs inside the template are deterministic so undo/redo history
// stays coherent across template swaps.

export const TEMPLATES: Template[] = [
  {
    id: "blank",
    name: "Blank",
    description: "Empty canvas. Start from scratch.",
    page: {
      id: "tpl-blank",
      title: "Untitled",
      slug: "/",
      sections: [{ id: "s1", columns: [{ id: "c1", widgets: [] }] }],
    },
  },
  {
    id: "hero-cta",
    name: "Hero + CTA",
    description: "Classic landing hero with headline, subtext and a call to action.",
    page: {
      id: "tpl-hero-cta",
      title: "Hero landing",
      slug: "/",
      sections: [
        {
          id: "hero",
          background: { kind: "color", color: "#0a0a0a" },
          padding: { top: 96, right: 24, bottom: 96, left: 24 },
          maxWidth: 860,
          columns: [
            {
              id: "c1",
              span: 12,
              gap: 24,
              widgets: [
                {
                  id: "h1",
                  type: "heading",
                  props: {
                    text: "Ship faster than your competition.",
                    level: 1,
                    align: "center",
                    color: "#fafafa",
                  },
                },
                {
                  id: "t1",
                  type: "text",
                  props: {
                    text: "Two sentences of positioning. What is the product. Who it is for. Nothing more.",
                    align: "center",
                    color: "#a3a3a3",
                    size: "lg",
                  },
                },
                {
                  id: "b1",
                  type: "button",
                  props: {
                    text: "Get started",
                    href: "#",
                    variant: "primary",
                    align: "center",
                  },
                },
              ],
            },
          ],
        },
      ],
    },
  },
  {
    id: "announcement",
    name: "Announcement",
    description: "Product announcement or launch banner with image support.",
    page: {
      id: "tpl-announcement",
      title: "Announcement",
      slug: "/",
      sections: [
        {
          id: "ann",
          background: { kind: "color", color: "#fef3c7" },
          padding: { top: 56, right: 24, bottom: 56, left: 24 },
          maxWidth: 780,
          columns: [
            {
              id: "c1",
              span: 12,
              gap: 16,
              widgets: [
                {
                  id: "kicker",
                  type: "text",
                  props: {
                    text: "NEW · April 22, 2026",
                    align: "center",
                    color: "#92400e",
                    size: "sm",
                  },
                },
                {
                  id: "h1",
                  type: "heading",
                  props: {
                    text: "Introducing something worth reading about.",
                    level: 2,
                    align: "center",
                    color: "#1c1917",
                  },
                },
                {
                  id: "img",
                  type: "image",
                  props: {
                    src: "https://placehold.co/900x480/fef3c7/92400e?text=Hero+image",
                    alt: "Product hero",
                    fit: "cover",
                    radius: 12,
                  },
                },
                {
                  id: "t1",
                  type: "text",
                  props: {
                    text: "A paragraph that frames the story. Tell users why this matters, in their words, not yours.",
                    align: "center",
                    color: "#57534e",
                    size: "base",
                  },
                },
                {
                  id: "b1",
                  type: "button",
                  props: {
                    text: "Read the announcement",
                    href: "#",
                    variant: "primary",
                    align: "center",
                  },
                },
              ],
            },
          ],
        },
      ],
    },
  },
  {
    id: "coming-soon",
    name: "Coming soon",
    description: "Minimalist pre-launch page with big statement and waitlist hint.",
    page: {
      id: "tpl-coming-soon",
      title: "Coming soon",
      slug: "/",
      sections: [
        {
          id: "cs",
          background: { kind: "color", color: "#0a0a0a" },
          padding: { top: 140, right: 24, bottom: 140, left: 24 },
          maxWidth: 900,
          columns: [
            {
              id: "c1",
              span: 12,
              gap: 28,
              widgets: [
                {
                  id: "h1",
                  type: "heading",
                  props: {
                    text: "Something new is coming.",
                    level: 1,
                    align: "center",
                    color: "#fafafa",
                  },
                },
                {
                  id: "t1",
                  type: "text",
                  props: {
                    text: "We're building it right now. Leave your email and be first when it ships.",
                    align: "center",
                    color: "#a3a3a3",
                    size: "lg",
                  },
                },
                {
                  id: "sp",
                  type: "spacer",
                  props: { height: 24 },
                },
                {
                  id: "b1",
                  type: "button",
                  props: {
                    text: "Join waitlist",
                    href: "#",
                    variant: "primary",
                    align: "center",
                  },
                },
              ],
            },
          ],
        },
      ],
    },
  },
  {
    id: "feature-grid",
    name: "Feature grid",
    description: "Heading row + three-column features grid.",
    page: {
      id: "tpl-feature-grid",
      title: "Features",
      slug: "/features",
      sections: [
        {
          id: "intro",
          padding: { top: 96, right: 24, bottom: 24, left: 24 },
          maxWidth: 900,
          columns: [
            {
              id: "c",
              span: 12,
              gap: 16,
              widgets: [
                {
                  id: "ih",
                  type: "heading",
                  props: {
                    text: "Three reasons people pay us.",
                    level: 2,
                    align: "center",
                  },
                },
                {
                  id: "it",
                  type: "text",
                  props: {
                    text: "Every feature below showed up in customer calls, not in a boardroom.",
                    align: "center",
                    color: "#525252",
                    size: "base",
                  },
                },
              ],
            },
          ],
        },
        {
          id: "grid",
          padding: { top: 32, right: 24, bottom: 96, left: 24 },
          maxWidth: 1100,
          columns: [
            {
              id: "g1",
              span: 4,
              gap: 12,
              widgets: [
                {
                  id: "g1h",
                  type: "heading",
                  props: { text: "Fast by default", level: 3 },
                },
                {
                  id: "g1t",
                  type: "text",
                  props: {
                    text: "Edge rendering, static HTML, zero runtime JS. Things open in under 200ms.",
                  },
                },
              ],
            },
            {
              id: "g2",
              span: 4,
              gap: 12,
              widgets: [
                {
                  id: "g2h",
                  type: "heading",
                  props: { text: "AI-native", level: 3 },
                },
                {
                  id: "g2t",
                  type: "text",
                  props: {
                    text: "Generate layouts, rewrite copy, suggest images — without leaving the editor.",
                  },
                },
              ],
            },
            {
              id: "g3",
              span: 4,
              gap: 12,
              widgets: [
                {
                  id: "g3h",
                  type: "heading",
                  props: { text: "Portable data", level: 3 },
                },
                {
                  id: "g3t",
                  type: "text",
                  props: {
                    text: "Everything is typed JSON. Export, version-control, pipe it anywhere.",
                  },
                },
              ],
            },
          ],
        },
      ],
    },
  },
  {
    id: "about",
    name: "About page",
    description: "Long-form about with alternating text and imagery.",
    page: {
      id: "tpl-about",
      title: "About",
      slug: "/about",
      sections: [
        {
          id: "top",
          padding: { top: 96, right: 24, bottom: 32, left: 24 },
          maxWidth: 780,
          columns: [
            {
              id: "c1",
              span: 12,
              gap: 20,
              widgets: [
                {
                  id: "kicker",
                  type: "text",
                  props: {
                    text: "ABOUT",
                    color: "#a855f7",
                    size: "sm",
                    align: "left",
                  },
                },
                {
                  id: "h1",
                  type: "heading",
                  props: {
                    text: "We build tools we would actually use.",
                    level: 1,
                    align: "left",
                  },
                },
                {
                  id: "t1",
                  type: "text",
                  props: {
                    text: "One or two short paragraphs about the team, what you value, and why this company exists. Human, specific. Not corporate.",
                    size: "lg",
                  },
                },
              ],
            },
          ],
        },
        {
          id: "img",
          padding: { top: 24, right: 24, bottom: 24, left: 24 },
          maxWidth: 1100,
          columns: [
            {
              id: "c1",
              span: 12,
              widgets: [
                {
                  id: "i1",
                  type: "image",
                  props: {
                    src: "https://placehold.co/1100x480/e5e5e5/737373?text=Team+photo",
                    alt: "Team",
                    fit: "cover",
                    radius: 12,
                  },
                },
              ],
            },
          ],
        },
        {
          id: "body",
          padding: { top: 32, right: 24, bottom: 96, left: 24 },
          maxWidth: 780,
          columns: [
            {
              id: "c1",
              span: 12,
              gap: 16,
              widgets: [
                {
                  id: "h2",
                  type: "heading",
                  props: { text: "Our principles", level: 2 },
                },
                {
                  id: "d",
                  type: "divider",
                  props: {
                    color: "#e5e5e5",
                    thickness: 1,
                    style: "solid",
                    spacing: 8,
                  },
                },
                {
                  id: "b1",
                  type: "text",
                  props: {
                    text: "One principle per paragraph. What you believe about the work, how you decide, what you will never do.",
                  },
                },
              ],
            },
          ],
        },
      ],
    },
  },
];

export function findTemplate(id: string): Template | undefined {
  return TEMPLATES.find((t) => t.id === id);
}

// Extra templates that exercise multi-column layouts and responsive
// overrides. Appended to TEMPLATES at the end of the file so the
// single-column starters stay on top of the gallery.
TEMPLATES.push(
  {
    id: "split-hero",
    name: "Split hero",
    description: "Two-column hero: copy on the left, image on the right.",
    page: {
      id: "tpl-split-hero",
      title: "Split hero",
      slug: "/",
      sections: [
        {
          id: "hero",
          padding: {
            desktop: { top: 96, right: 40, bottom: 96, left: 40 },
            mobile: { top: 56, right: 20, bottom: 56, left: 20 },
          },
          maxWidth: 1200,
          columns: [
            {
              id: "left",
              span: { desktop: 6, mobile: 12 },
              gap: 20,
              widgets: [
                {
                  id: "kicker",
                  type: "text",
                  props: {
                    text: "NEW",
                    color: "#a855f7",
                    size: "sm",
                  },
                },
                {
                  id: "h1",
                  type: "heading",
                  props: {
                    text: "A product worth telling your team about.",
                    level: 1,
                  },
                },
                {
                  id: "sub",
                  type: "text",
                  props: {
                    text: "Two or three sentences of value proposition. Keep it plain. Human.",
                    size: "lg",
                    color: "#525252",
                  },
                },
                {
                  id: "cta",
                  type: "button",
                  props: {
                    text: "Try it free",
                    href: "#",
                    variant: "primary",
                  },
                },
              ],
            },
            {
              id: "right",
              span: { desktop: 6, mobile: 12 },
              widgets: [
                {
                  id: "img",
                  type: "image",
                  props: {
                    src: "https://placehold.co/720x520/e5e5e5/a855f7?text=Product+shot",
                    alt: "Product",
                    fit: "cover",
                    radius: 14,
                  },
                },
              ],
            },
          ],
        },
      ],
    },
  },
  {
    id: "pricing",
    name: "Pricing (3 tiers)",
    description: "Three-column pricing with per-plan features.",
    page: {
      id: "tpl-pricing",
      title: "Pricing",
      slug: "/pricing",
      sections: [
        {
          id: "intro",
          padding: { top: 96, right: 24, bottom: 24, left: 24 },
          maxWidth: 800,
          columns: [
            {
              id: "c",
              span: 12,
              gap: 12,
              widgets: [
                {
                  id: "h",
                  type: "heading",
                  props: {
                    text: "Simple pricing. Honest limits.",
                    level: 2,
                    align: "center",
                  },
                },
                {
                  id: "t",
                  type: "text",
                  props: {
                    text: "Start free. Upgrade when the free tier stops covering your needs.",
                    align: "center",
                    color: "#525252",
                  },
                },
              ],
            },
          ],
        },
        {
          id: "grid",
          padding: { top: 32, right: 24, bottom: 96, left: 24 },
          maxWidth: 1100,
          columns: [
            {
              id: "free",
              span: { desktop: 4, mobile: 12 },
              gap: 12,
              widgets: [
                {
                  id: "fh",
                  type: "heading",
                  props: { text: "Free", level: 3 },
                },
                {
                  id: "fp",
                  type: "heading",
                  props: { text: "$0", level: 2, color: "#0a0a0a" },
                },
                {
                  id: "ft",
                  type: "text",
                  props: {
                    text: "For side projects and small experiments.",
                    color: "#525252",
                  },
                },
                {
                  id: "fd",
                  type: "divider",
                  props: { color: "#e5e5e5", spacing: 4 },
                },
                {
                  id: "fb",
                  type: "button",
                  props: {
                    text: "Start free",
                    href: "#",
                    variant: "secondary",
                    fullWidth: true,
                  },
                },
              ],
            },
            {
              id: "pro",
              span: { desktop: 4, mobile: 12 },
              gap: 12,
              widgets: [
                {
                  id: "ph",
                  type: "heading",
                  props: { text: "Pro", level: 3 },
                },
                {
                  id: "pp",
                  type: "heading",
                  props: { text: "$19 / mo", level: 2, color: "#a855f7" },
                },
                {
                  id: "pt",
                  type: "text",
                  props: {
                    text: "For growing teams that need more.",
                    color: "#525252",
                  },
                },
                {
                  id: "pd",
                  type: "divider",
                  props: { color: "#e5e5e5", spacing: 4 },
                },
                {
                  id: "pb",
                  type: "button",
                  props: {
                    text: "Start Pro trial",
                    href: "#",
                    variant: "primary",
                    fullWidth: true,
                  },
                },
              ],
            },
            {
              id: "biz",
              span: { desktop: 4, mobile: 12 },
              gap: 12,
              widgets: [
                {
                  id: "bh",
                  type: "heading",
                  props: { text: "Business", level: 3 },
                },
                {
                  id: "bp",
                  type: "heading",
                  props: { text: "$49 / mo", level: 2, color: "#0a0a0a" },
                },
                {
                  id: "bt",
                  type: "text",
                  props: {
                    text: "SLAs, SSO, priority support.",
                    color: "#525252",
                  },
                },
                {
                  id: "bd",
                  type: "divider",
                  props: { color: "#e5e5e5", spacing: 4 },
                },
                {
                  id: "bb",
                  type: "button",
                  props: {
                    text: "Contact sales",
                    href: "#",
                    variant: "secondary",
                    fullWidth: true,
                  },
                },
              ],
            },
          ],
        },
      ],
    },
  },
  {
    id: "testimonial-grid",
    name: "Testimonial grid",
    description: "Heading + three columns of quotes from real users.",
    page: {
      id: "tpl-testimonial-grid",
      title: "What people say",
      slug: "/testimonials",
      sections: [
        {
          id: "intro",
          padding: { top: 72, right: 24, bottom: 16, left: 24 },
          maxWidth: 800,
          columns: [
            {
              id: "c",
              span: 12,
              widgets: [
                {
                  id: "h",
                  type: "heading",
                  props: {
                    text: "Real people. Real quotes.",
                    level: 2,
                    align: "center",
                  },
                },
              ],
            },
          ],
        },
        {
          id: "grid",
          padding: { top: 32, right: 24, bottom: 96, left: 24 },
          maxWidth: 1200,
          columns: [
            {
              id: "q1",
              span: { desktop: 4, mobile: 12 },
              gap: 10,
              widgets: [
                {
                  id: "q1t",
                  type: "text",
                  props: {
                    text: "\u201CI ship in a day what used to take me a week.\u201D",
                    size: "lg",
                  },
                },
                {
                  id: "q1a",
                  type: "text",
                  props: {
                    text: "— Ana, designer",
                    color: "#737373",
                    size: "sm",
                  },
                },
              ],
            },
            {
              id: "q2",
              span: { desktop: 4, mobile: 12 },
              gap: 10,
              widgets: [
                {
                  id: "q2t",
                  type: "text",
                  props: {
                    text: "\u201COur marketing site stopped being a bottleneck.\u201D",
                    size: "lg",
                  },
                },
                {
                  id: "q2a",
                  type: "text",
                  props: {
                    text: "— Jamal, founder",
                    color: "#737373",
                    size: "sm",
                  },
                },
              ],
            },
            {
              id: "q3",
              span: { desktop: 4, mobile: 12 },
              gap: 10,
              widgets: [
                {
                  id: "q3t",
                  type: "text",
                  props: {
                    text: "\u201CSomeone on the team who isn\u2019t a dev ships changes now.\u201D",
                    size: "lg",
                  },
                },
                {
                  id: "q3a",
                  type: "text",
                  props: {
                    text: "— Pavel, eng manager",
                    color: "#737373",
                    size: "sm",
                  },
                },
              ],
            },
          ],
        },
      ],
    },
  },
  {
    id: "newsletter",
    name: "Newsletter signup",
    description: "Focused page: big statement + call to subscribe.",
    page: {
      id: "tpl-newsletter",
      title: "Subscribe",
      slug: "/subscribe",
      sections: [
        {
          id: "hero",
          background: { kind: "color", color: "#f5f3ff" },
          padding: {
            desktop: { top: 120, right: 24, bottom: 120, left: 24 },
            mobile: { top: 72, right: 20, bottom: 72, left: 20 },
          },
          maxWidth: 780,
          columns: [
            {
              id: "c",
              span: 12,
              gap: 24,
              widgets: [
                {
                  id: "kicker",
                  type: "text",
                  props: {
                    text: "WEEKLY DIGEST",
                    color: "#7c3aed",
                    size: "sm",
                    align: "center",
                  },
                },
                {
                  id: "h1",
                  type: "heading",
                  props: {
                    text: "One email. Fridays. The best of the week.",
                    level: 1,
                    align: "center",
                  },
                },
                {
                  id: "t",
                  type: "text",
                  props: {
                    text: "No algorithm tricks. Just five links, three sentences each, curated by hand.",
                    align: "center",
                    color: "#525252",
                    size: "lg",
                  },
                },
                {
                  id: "cta",
                  type: "button",
                  props: {
                    text: "Subscribe (free)",
                    href: "#",
                    variant: "primary",
                    align: "center",
                  },
                },
                {
                  id: "note",
                  type: "text",
                  props: {
                    text: "Unsubscribe in one click. We never share your email.",
                    align: "center",
                    color: "#737373",
                    size: "sm",
                  },
                },
              ],
            },
          ],
        },
      ],
    },
  },
);
